import { useEffect, useRef } from "react";
import { triggerAI } from "../lib/api";
import { isVisibleResponse } from "../lib/responses";
import { useClusters } from "./useClusters";
import { useResponses } from "./useResponses";
import type { Question } from "../lib/types";

interface Props {
  question: Question | null;
  /** The event's recluster_threshold. Defaults to 5 if not provided. */
  reclusterThreshold?: number;
  accessToken: string;
  enabled?: boolean;
}

/**
 * Auto-recluster (facilitated mode) when enough NEW visible responses have
 * arrived since the last cluster run / re-cluster attempt.
 *
 * Stage Calm: trigger switches from "uncategorized count >= threshold" to
 * "(visible count) - (count at last run attempt) >= threshold". The old
 * uncategorizedCount-based gate thrashed during re-cluster windows because
 * the cluster path resets all cluster_id values to NULL transiently. New
 * submissions are the only real driver.
 *
 * Trigger semantics (after the initial cluster_run has happened for this question):
 *  - newSinceLastClusterRun = visibleResponses.length - lastClusterCountRef
 *  - If newSinceLastClusterRun >= reclusterThreshold AND question.is_frozen == false,
 *    trigger a full re-cluster.
 *  - The initial cluster_run is NOT driven by this hook — facilitator triggers
 *    the first cluster manually (existing UX).
 */
export function useAutoCluster({
  question,
  reclusterThreshold = 5,
  accessToken,
  enabled = true,
}: Props) {
  const { responses } = useResponses(question?.id ?? null);
  const { clusters } = useClusters(question?.id ?? null);
  const runningRef = useRef<boolean>(false);
  const lastSeenRunIdRef = useRef<string | null>(null);
  const lastClusterCountRef = useRef<number>(0);

  const visibleResponses = responses.filter((r) => isVisibleResponse(r));
  const latestRunId = clusters[0]?.run_id ?? null;

  // Snapshot the latest run AND its visible-count baseline when a fresh run
  // arrives (manual cluster, auto-recluster fired by us, or external recluster).
  // This is what makes the gate self-healing: a manual recluster from the
  // facilitator console resets the anchor cleanly without needing us to know
  // the trigger source.
  useEffect(() => {
    if (latestRunId && latestRunId !== lastSeenRunIdRef.current) {
      lastSeenRunIdRef.current = latestRunId;
      lastClusterCountRef.current = visibleResponses.length;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestRunId]);

  useEffect(() => {
    if (!enabled || !question) return;
    if (runningRef.current) return;
    if (!latestRunId) return; // must wait for initial cluster
    if (question.is_frozen) return; // freeze blocks auto re-cluster

    const newSinceLastClusterRun = visibleResponses.length - lastClusterCountRef.current;
    if (newSinceLastClusterRun < reclusterThreshold) return;

    const run = async () => {
      runningRef.current = true;
      try {
        await triggerAI({
          type: "cluster",
          questionId: question.id,
          accessToken,
          triggerReason: "auto_threshold",
        });
        // Anchor to attempts: a 502 from validation (Task 3) still counts as
        // an attempt and we don't want to immediately re-fire on the next tick.
        lastClusterCountRef.current = visibleResponses.length;
      } catch (err) {
        console.error("[useAutoCluster] auto re-cluster failed", err);
      } finally {
        runningRef.current = false;
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleResponses.length, question?.id, question?.is_frozen, latestRunId, enabled, reclusterThreshold]);

  // Reset when the question changes.
  useEffect(() => {
    lastSeenRunIdRef.current = null;
    lastClusterCountRef.current = 0;
    runningRef.current = false;
  }, [question?.id]);
}
